import "@/index.css"; // need to be imported before primevue
import { useSeoMeta } from "@unhead/vue";
import { createHead } from "@unhead/vue/client";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { ConfirmationService } from "primevue";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import Tooltip from "primevue/tooltip";
import { getLocaleBasedOfPreferredLanguages } from "shared-core/services/i18n.ts";
import { OrganizationType } from "shared-core/types/organization.ts";
import { Platform } from "shared-core/types/platform";
import { loadLocaleNamespaceFromVite } from "shared-ui/services/i18n-locale-loader.ts";
import { createI18nRuntime } from "shared-ui/services/i18n-runtime.ts";
import { PlatformConfigKey } from "shared-ui/services/platform.ts";
import { I18nRuntimeKey } from "shared-ui/services/vue-i18n.ts";
import { vueErrorHandler } from "shared-ui/utils/vue-error-handler.ts";
import { type Component, createApp, shallowRef } from "vue";
import App from "@/App.vue";
import layouts from "@/layouts";
import { FeatureFlagsPlugin } from "@/plugins/feature-flags";
import { router } from "@/router";
import { initOpenInApp } from "@/services/open-in-app.ts";
import { useAuthStore } from "@/stores/auth-store.ts";
import { Api } from "@/utils/api";
import { syncLayoutWithRoute } from "@/utils/route-layout.ts";

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
const app = createApp(App);

app.use(pinia);
app.use(PrimeVue, {
  theme: "none",
});

const i18nRuntime = createI18nRuntime(loadLocaleNamespaceFromVite, {
  getOrganizationType: () =>
    useAuthStore(pinia).user?.organizationType ?? OrganizationType.BUSINESS,
});
const { i18n, setLocale } = i18nRuntime;
app.use(i18n);
app.provide(I18nRuntimeKey, i18nRuntime);

const locale = getLocaleBasedOfPreferredLanguages(
  navigator.language,
  navigator.languages,
);
i18n.global.locale.value = locale;
app.use(new Api(() => i18n.global.locale.value));

const head = createHead({
  init: [
    {
      title: "Loading",
      titleTemplate: "%s | Controla",
      htmlAttrs: { lang: locale },
    },
  ],
});
app.use(head);

app.use(ToastService);
app.use(ConfirmationService);

app.directive("tooltip", Tooltip);

// Inspiration from: https://itnext.io/3-ways-to-create-a-multiple-layouts-system-vue-3-b24c0736b7bb
const layout = shallowRef<Component | string>("div");
app.provide("app:layout", layout);
syncLayoutWithRoute(router, layout, layouts);

initOpenInApp(router);

app.provide(PlatformConfigKey, {
  platform: Platform.Web,
  openExternalUrl: (url: string) => {
    window.open(url, "_blank");
    return Promise.resolve();
  },
  backendBaseUrl: "",
  seoHandler: {
    setMeta: useSeoMeta,
  },
});

app.use(router);
app.use(FeatureFlagsPlugin, router);

app.config.errorHandler = (
  err: unknown,
  instance: unknown,
  info: string,
): void => {
  vueErrorHandler(
    err,
    instance,
    info,
    console.error,
    router.currentRoute.value,
  );
};

app.mount("#app");

await setLocale(locale);

// Lazy-load Google Fonts after first paint, even if requestIdleCallback doesn't fire
(function loadFonts(): void {
  const href =
    "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Lora:ital,wght@0,400..700;1,400..700&display=swap";

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;

  // Make it invisible to rendering until loaded, then flip on
  link.media = "print";
  link.onload = (): void => {
    link.media = "all";
  };

  const schedule = (fn: () => void): void => {
    if ("requestIdleCallback" in window) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).requestIdleCallback(fn, { timeout: 2000 });
    } else {
      // run soon after mount if RIC is unavailable/blocked
      setTimeout(fn, 0);
    }
  };

  schedule(() => document.head.appendChild(link));
})();
